import { useRef, useEffect } from "react";
import { gsap } from "../../lib/gsap";
import Container from "../layout/Container";
import Section from "../layout/Section";

import sensorsImg from "../../assets/images/autonomy/sensors.png";
import decisionImg from "../../assets/images/autonomy/decision.jpg";

/* ========================= */
/* Metric Component */
/* ========================= */
function Metric({ value, label }) {
  const ref = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      ref.current,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 90%",
        },
      }
    );
  }, []);

  return (
    <div
      ref={ref}
      className="group relative p-8 rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur-md transition-all duration-500 hover:border-[color:var(--color-accent)]"
    >
      <h4 className="text-3xl md:text-4xl font-semibold text-[color:var(--color-accent)]">
        {value}
      </h4>

      <p className="text-slate-500 mt-3 text-sm tracking-wide">
        {label}
      </p>

      {/* Subtle hover glow */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-indigo-500/10 to-transparent pointer-events-none"></div>
    </div>
  );
}

/* ========================= */
/* Autonomy Section */
/* ========================= */
export default function Autonomy() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { opacity: 0, y: 80 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 85%",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section
      id="autonomy"
      className="relative bg-slate-950 text-white py-32"
    >
      <Container>
        <div ref={sectionRef} className="space-y-28">

          {/* Header Block */}
          <div className="max-w-4xl">
            <h2 className="text-4xl md:text-6xl font-semibold leading-tight mb-8">
              Autonomous Mobility Platform
            </h2>

            <p className="text-slate-400 text-lg leading-relaxed">
              Our end-to-end autonomy architecture integrates real-time
              perception, predictive intelligence, and deterministic
              control loops engineered for complex urban and highway
              environments.
            </p>
          </div>

          {/* Capability 1 */}
          <div className="grid md:grid-cols-2 gap-16 items-center">

            <div className="rounded-3xl overflow-hidden border border-slate-800">
              <img
                src={sensorsImg}
                alt="Multi-sensor fusion system"
                className="w-full object-cover"
              />
            </div>

            <div className="space-y-6">
              <h3 className="text-2xl font-semibold">
                Multi-Sensor Fusion
              </h3>

              <p className="text-slate-400 leading-relaxed">
                LiDAR, radar, camera, and ultrasonic streams are fused
                into a unified 360° spatial awareness model operating
                at ultra-low latency.
              </p>

              <ul className="space-y-3 text-slate-500">
                <li>• 3D object detection & segmentation</li>
                <li>• HD lane-level localization</li>
                <li>• Occlusion & depth reasoning</li>
              </ul>
            </div>
          </div>

          {/* Capability 2 */}
          <div className="grid md:grid-cols-2 gap-16 items-center">

            <div className="space-y-6">
              <h3 className="text-2xl font-semibold">
                Predictive Decision Intelligence
              </h3>

              <p className="text-slate-400 leading-relaxed">
                Multi-agent trajectory prediction models anticipate
                dynamic traffic behavior to enable safe and proactive
                motion planning.
              </p>

              <ul className="space-y-3 text-slate-500">
                <li>• Behavioral forecasting engines</li>
                <li>• Risk-aware planning algorithms</li>
                <li>• Deterministic control validation</li>
              </ul>
            </div>

            <div className="rounded-3xl overflow-hidden border border-slate-800">
              <img
                src={decisionImg}
                alt="Decision intelligence system"
                className="w-full object-cover"
              />
            </div>
          </div>

          {/* Advanced Metrics Grid */}
          <div className="pt-20 border-t border-slate-800">

            <div className="grid md:grid-cols-4 gap-12 text-center">

              <Metric value="10ms" label="Inference Latency" />
              <Metric value="360°" label="Perception Coverage" />
              <Metric value="97.999%" label="Operational Uptime" />
              <Metric value="12B+" label="Model Parameters" />

              <Metric value="4TB/hr" label="Sensor Data Throughput" />
              <Metric value="8" label="Redundancy Layers" />
              <Metric value="2.3M km" label="Validation Distance" />
              <Metric value="500+ Hz" label="Sensor Fusion Rate" />

            </div>

          </div>

        </div>
      </Container>
    </Section>
  );
}